import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  foyerApi,
  type VodDownloadPlan,
  type VodTitleRow,
} from "../../http/foyer";
import { tokens } from "../../theme";
import { useFormat, type Formatters } from "../../i18n/format";
import { translateKey, type Translate } from "../../i18n/translate";

/**
 * "Download" on a VOD title: say what will be downloaded, then hand the URL to
 * the browser.
 *
 * The plan is read first because the download itself is a plain navigation
 * into the browser's download manager - the only way a multi-gigabyte film
 * streams to disk instead of into this tab's memory - and a navigation that
 * fails has nowhere to show a reason. So every refusal the server can give
 * (encrypted only, DASH only, CDN refused the manifest, no ffmpeg on the host)
 * is shown HERE, before the operator commits.
 */

type PlanError = {
  code?: string;
  message: string;
  body?: { hint?: string; detail?: string; status?: number };
};

export function planErrorText(t: Translate, err: PlanError): string {
  const body = err.body ?? {};
  const k = (name: string, params?: Record<string, unknown>) =>
    translateKey(t, "content:vodDownloadDialog.error." + name, params);
  switch (err.code) {
    case "download_refused":
      return body.hint ?? k("download_refused");
    case "manifest_refused":
      return body.status
        ? k("manifest_refused_status", { status: body.status })
        : k("manifest_refused");
    case "manifest_invalid":
      return body.detail
        ? k("manifest_invalid_detail", { detail: body.detail })
        : k("manifest_invalid");
    case "manifest_unreachable":
    case "manifest_url_invalid":
    case "not_found":
      return k(err.code);
    default:
      return body.hint ?? err.message;
  }
}

function megabits(
  t: Translate,
  fmt: Formatters,
  bps: number | null
): string | null {
  if (!bps) return null;
  const digits = bps >= 10_000_000 ? 0 : 1;
  return translateKey(t, "content:vodDownloadDialog.megabits", {
    value: fmt.number(bps / 1_000_000, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }),
  });
}

function audioText(t: Translate, plan: VodDownloadPlan): string {
  const tracks = plan.audio.map((a) =>
    a.name && a.language
      ? `${a.name} (${a.language})`
      : (a.name ??
        a.language ??
        translateKey(t, "content:vodDownloadDialog.unnamedTrack"))
  );
  if (plan.muxedAudio)
    tracks.unshift(translateKey(t, "content:vodDownloadDialog.muxedAudio"));
  return tracks.join(", ");
}

const SEPARATOR = " · ";

/** Hand a URL to the browser's download manager. A seam for tests. */
export function startBrowserDownload(url: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = "";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function VodDownloadDialog({
  title,
  onClose,
  download = startBrowserDownload,
}: {
  title: VodTitleRow | null;
  onClose: () => void;
  download?: (url: string) => void;
}) {
  const { t } = useTranslation(["content", "common"]);
  const fmt = useFormat();
  const [plan, setPlan] = useState<VodDownloadPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const assetId = title?.assetId ?? null;

  useEffect(() => {
    if (!assetId) return;
    let cancelled = false;
    setPlan(null);
    setError(null);
    foyerApi
      .vodDownloadPlan(assetId)
      .then((p) => !cancelled && setPlan(p))
      .catch((e: PlanError) => !cancelled && setError(planErrorText(t, e)));
    return () => {
      cancelled = true;
    };
  }, [assetId, t]);

  const loading = Boolean(title) && !plan && !error;
  const size =
    plan?.video.width && plan.video.height
      ? `${plan.video.width}×${plan.video.height}`
      : t("vodDownloadDialog.singleRendition");

  return (
    <Dialog open={title !== null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("vodDownloadDialog.title", {
          name: title?.originalTitle || t("vodDownloadDialog.noName"),
        })}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ py: 2 }}
          >
            <CircularProgress size={18} />
            <Typography variant="body2">
              {t("vodDownloadDialog.reading")}
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" data-testid="vod-download-error">
            {error}
          </Alert>
        )}

        {plan && (
          <Stack spacing={1.5} data-testid="vod-download-plan">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "max-content 1fr",
                columnGap: 2,
                rowGap: 0.75,
                typography: "body2",
              }}
            >
              <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
                {t("vodDownloadDialog.video")}
              </Typography>
              <Typography variant="body2" data-testid="vod-download-video">
                {[size, megabits(t, fmt, plan.video.bandwidth)]
                  .filter(Boolean)
                  .join(SEPARATOR)}
              </Typography>
              <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
                {t("vodDownloadDialog.audio")}
              </Typography>
              <Typography variant="body2" data-testid="vod-download-audio">
                {audioText(t, plan) || t("vodDownloadDialog.noAudio")}
              </Typography>
              <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
                {t("vodDownloadDialog.file")}
              </Typography>
              <Typography
                variant="bodySm"
                sx={{ fontFamily: "fontFamilyMono", wordBreak: "break-all" }}
              >
                {plan.fileName}
              </Typography>
            </Box>

            {plan.skippedEncrypted && (
              <Alert severity="info" sx={{ typography: "bodySm" }}>
                {t("vodDownloadDialog.alsoEncrypted")}
              </Alert>
            )}
            {!plan.available && (
              <Alert severity="warning" sx={{ typography: "bodySm" }}>
                {t("vodDownloadDialog.noFfmpeg")}
              </Alert>
            )}
            {plan.available && plan.busy && (
              <Alert severity="warning" sx={{ typography: "bodySm" }}>
                {t("vodDownloadDialog.busy")}
              </Alert>
            )}

            <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
              {t("vodDownloadDialog.explanation")}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common:buttons.cancel")}</Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={!plan || !plan.available || plan.busy}
          data-testid="vod-download-start"
          onClick={() => {
            if (!title) return;
            download(foyerApi.vodDownloadUrl(title.assetId));
            onClose();
          }}
        >
          {t("common:buttons.download")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
